import React, { useEffect, useState } from 'react';
import { Button, Switch, Typography, Tabs, message, Spin } from 'antd';
import './styles.css';
import styled from 'styled-components';
import CollectAttributes from './CollectAttributes';
import CssSelectors from './CssSelectors';
import { useGetClickTracking, usePostClickTracking } from '@src/api/emp/queryHooks';

const { Text } = Typography;
const { TabPane } = Tabs;

const StyledTabs = styled(Tabs)`
  margin-top: 16px;

  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-tab {
    font-size: 16px;
    font-weight: 500;
  }

  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #0046ff !important;
  }

  .ant-tabs-ink-bar {
    background: #0046ff;
  }
`;

const ClickTracking = () => {
  const onChange = (checked: boolean) => {
    createClickTracking(
      { queryBody: { en: checked } },
      {
        onSuccess: () => {
          refetchClickTrackingData();
          message.success({
            content: (
              <div style={{ textAlign: 'left' }}>
                Successfully {checked ? 'enabled' : 'disabled'} click tracking.
                <br />
                Sensors may take up to 5 minutes to reflect changes.
              </div>
            ),
            duration: 5,
          });
        },
        onError: (error) => {
          const errorMsg =
            error?.response?.data?.msg ||
            error?.msg ||
            error?.message ||
            'An unexpected error occurred.';
          message.error(errorMsg);
        },
      },
    );
  };

  const {
    data: clickTrackingData,
    isLoading: isLoadingClickTrackingData,
    refetch: refetchClickTrackingData,
    isFetching: isFetchingClickTrackingData,
  } = useGetClickTracking();

  useEffect(() => {
    refetchClickTrackingData();
  }, [refetchClickTrackingData]);
  const { mutate: createClickTracking, isLoading: isLoadingCreateClickTracking } =
    usePostClickTracking();

  const [isExpanded, setIsExpanded] = useState(false);
  const [unsavedChangesForAttributes, setUnsavedChangesForAttributes] = useState(0);
  const [unsavedChangesForCssSelectors, setUnsavedChangesForCssSelectors] = useState(0);
  return (
    <Spin spinning={isLoadingCreateClickTracking || isLoadingClickTrackingData}>
      <div style={{ padding: '8px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: '20px', fontWeight: '500' }} data-test-id="clickTrackingHeader">
            Auto Collection of Clicks
          </span>
          <Switch
            data-test-id="clickTrackingToggle"
            size="small"
            onChange={onChange}
            style={{ marginLeft: '16px' }}
            checked={clickTrackingData?.en?.value}
          />
        </div>

        <div style={{ width: '100%', marginTop: 8 }}>
          <Text
            style={{ fontSize: '14px', color: '#8c8c8c', fontWeight: 400 ,width: '95%'}}
            data-test-id="clickTrackingDescription"
          >
            Customize the user-click tracking across your application, beyond buttons and links. In
            the Custom Attribute section, specify non-standard HTML attributes (such as data-test-id
            or app-custom-button-id) using their key value. In the CSS Selector section, define
            tags, classes, IDs, or attributes to precisely target the elements you want to track.
          </Text>
        </div>
        {!clickTrackingData?.en?.value && clickTrackingData?.en?.metadata?.updated_at ? (
          <div
            style={{
              marginTop: '24px',
              color: '#8c8c8c',
              fontSize: 14,
              fontWeight: 400,
            }}
            data-test-id="clickTrackingLastUpdated"
          >
            Last Updated on{' '}
            {new Date(clickTrackingData?.en?.metadata?.updated_at * 1000).toLocaleDateString(
              'en-US',
              {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              },
            )}{' '}
            by {clickTrackingData?.en?.metadata?.updated_by}
          </div>
        ) : null}
        <StyledTabs
          defaultActiveKey="1"
          tabPosition="top"
          style={{
            pointerEvents: clickTrackingData?.en?.value ? 'auto' : 'none',
            opacity: clickTrackingData?.en?.value ? 1 : 0.3,
          }}
        >
          <TabPane
            tab={
              <span>
                Collect Attributes{' '}
                {unsavedChangesForAttributes > 0 && (
                  <span
                    style={{
                      backgroundColor: 'rgba(255, 231, 186, 1)',
                      color: 'rgba(173, 78, 0, 1)',
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontSize: 12,
                      marginLeft: 4,
                      fontWeight: 500,
                    }}
                  >
                    {unsavedChangesForAttributes}
                  </span>
                )}
              </span>
            }
            key="1"
            data-test-id="collectAttributesTab"
          >
            <div
              style={{
                height: 'calc(100vh - 330px)',
                overflowY: 'auto',
                paddingRight: 16,
                pointerEvents: clickTrackingData?.en?.value ? 'auto' : 'none',
              }}
            >
              <CollectAttributes
                collectAttributesData={clickTrackingData?.collectAttr.value}
                collectAttributesMetadata={clickTrackingData?.collectAttr.metadata}
                refetchClickTracking={refetchClickTrackingData}
                setUnsavedChangesForAttributes={setUnsavedChangesForAttributes}
                isLoadingClickTrackingData={isFetchingClickTrackingData}
              />
            </div>
          </TabPane>
          <TabPane
            tab={
              <span>
                CSS Selectors{' '}
                {unsavedChangesForCssSelectors > 0 && (
                  <span
                    style={{
                      backgroundColor: 'rgba(255, 231, 186, 1)',
                      color: 'rgba(173, 78, 0, 1)',
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontSize: 12,
                      marginLeft: 4,
                      fontWeight: 500,
                    }}
                  >
                    {unsavedChangesForCssSelectors}
                  </span>
                )}
              </span>
            }
            key="2"
            data-test-id="cssSelectorsTab"
          >
            <div
              style={{
                height: 'calc(100vh - 330px)',
                overflowY: 'auto',
                paddingRight: 16,
                pointerEvents: clickTrackingData?.en?.value ? 'auto' : 'none',
              }}
            >
              <CssSelectors
                cssSelectorsData={clickTrackingData?.cssSelectors}
                refetchClickTracking={refetchClickTrackingData}
                setUnsavedChangesForCssSelectors={setUnsavedChangesForCssSelectors}
                isLoadingClickTrackingData={isFetchingClickTrackingData}
              />
            </div>
          </TabPane>
        </StyledTabs>
      </div>
    </Spin>
  );
};

export default ClickTracking;
